import { useCallback, useEffect, useState } from 'react';
import { useDomainInventoryListener } from './useDomainInventoryListener';
import { 
  getProductValueOptimized,
  getProductQuantityOptimized,
  getProductPotentialProfitOptimized,
  getLotsForProduct,
  clearLotCache,
  dispatchCentralUpdate
} from '../utils/lotSystem';

/**
 * Einheitlicher Lot-System Hook für alle Komponenten
 * Stellt konsistente Lot-System Integration bereit
 */
export const useLotSystem = (productId = null, location = null) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Zentrale Lot-System Funktionen
  const getValue = useCallback((id = productId, loc = location) => {
    try {
      return getProductValueOptimized(id, loc);
    } catch (err) {
      console.error('Error getting product value:', err);
      setError(err);
      return 0;
    }
  }, [productId, location]);

  const getQuantity = useCallback((id = productId, loc = location) => {
    try {
      return getProductQuantityOptimized(id, loc);
    } catch (err) {
      console.error('Error getting product quantity:', err);
      setError(err);
      return 0;
    }
  }, [productId, location]);

  const getPotentialProfit = useCallback((id = productId, loc = location) => {
    try {
      return getProductPotentialProfitOptimized(id, loc);
    } catch (err) {
      console.error('Error getting product potential profit:', err);
      setError(err);
      return 0;
    }
  }, [productId, location]);

  const getLots = useCallback((id = productId, loc = location) => {
    try {
      return getLotsForProduct(id, loc);
    } catch (err) {
      console.error('Error getting lots:', err);
      setError(err);
      return [];
    }
  }, [productId, location]);

  const clearCache = useCallback((id = productId) => {
    try {
      clearLotCache(id);
    } catch (err) {
      console.error('Error clearing cache:', err);
      setError(err);
    }
  }, [productId]);

  const dispatchUpdate = useCallback((id = productId, action = 'update', data = {}) => {
    try {
      dispatchCentralUpdate(id, action, data);
    } catch (err) {
      console.error('Error dispatching update:', err);
      setError(err);
    }
  }, [productId]);

  // Automatische Event-Handler für Updates
  useDomainInventoryListener((detail) => {
    if (detail.productId === productId) {
      clearCache(productId);
      setIsLoading(false);
    }
  }, [productId, clearCache]);

  return {
    // Lot-System Funktionen
    getValue,
    getQuantity,
    getPotentialProfit,
    getLots,
    clearCache,
    dispatchUpdate,
    
    // Status
    isLoading,
    error,
    
    // Utility
    refresh: () => clearCache(productId)
  };
};

/**
 * Hook für mehrere Produkte
 */
export const useLotSystemMultiple = (productIds = []) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const getValues = useCallback(() => {
    const values = {};
    productIds.forEach(id => {
      try {
        values[id] = getProductValueOptimized(id, null);
      } catch (err) {
        console.error(`Error getting value for product ${id}:`, err);
        values[id] = 0;
      }
    });
    return values;
  }, [productIds]);

  const getQuantities = useCallback(() => {
    const quantities = {};
    productIds.forEach(id => {
      try {
        quantities[id] = getProductQuantityOptimized(id, null);
      } catch (err) {
        console.error(`Error getting quantity for product ${id}:`, err);
        quantities[id] = 0;
      }
    });
    return quantities;
  }, [productIds]);

  const getPotentialProfits = useCallback(() => {
    const profits = {};
    productIds.forEach(id => {
      try {
        profits[id] = getProductPotentialProfitOptimized(id, null);
      } catch (err) {
        console.error(`Error getting potential profit for product ${id}:`, err);
        profits[id] = 0;
      }
    });
    return profits;
  }, [productIds]);

  const clearAllCaches = useCallback(() => {
    productIds.forEach(id => {
      try {
        clearLotCache(id);
      } catch (err) {
        console.error(`Error clearing cache for product ${id}:`, err);
      }
    });
  }, [productIds]);

  // Automatische Event-Handler für Updates
  useDomainInventoryListener((detail) => {
    if (productIds.includes(detail.productId)) {
      clearLotCache(detail.productId);
      setIsLoading(false);
    }
  }, [productIds]);

  return {
    // Lot-System Funktionen
    getValues,
    getQuantities,
    getPotentialProfits,
    clearAllCaches,
    
    // Status
    isLoading,
    error,
    
    // Utility
    refresh: clearAllCaches
  };
};

/**
 * Hook für Lot-System Integration in Komponenten
 */
export const useLotSystemIntegration = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Zentrale Update-Funktion
  const updateProduct = useCallback((productId, updates) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Dispatch update through central system
      dispatchCentralUpdate(productId, 'update', updates);
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error updating product:', err);
      setError(err);
      setIsLoading(false);
    }
  }, []);

  // Batch update function
  const updateProducts = useCallback((updates) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Process each update
      Object.entries(updates).forEach(([productId, productUpdates]) => {
        dispatchCentralUpdate(Number(productId), 'batch_update', productUpdates);
      });
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error updating products:', err);
      setError(err);
      setIsLoading(false);
    }
  }, []);

  return {
    updateProduct,
    updateProducts,
    isLoading,
    error
  };
};

/**
 * Einheitlicher Hook für alle Komponenten
 * Kombiniert alle Lot-System Funktionen
 */
export const useUnifiedLotSystem = () => {
  const lotSystemIntegration = useLotSystemIntegration();
  
  return {
    // Integration functions
    ...lotSystemIntegration,
    
    // Utility functions
    isReady: true,
    
    // Batch operations
    batchUpdate: lotSystemIntegration.updateProducts,
    
    // Single operations
    update: lotSystemIntegration.updateProduct,
    
    // Status
    isLoading: lotSystemIntegration.isLoading,
    error: lotSystemIntegration.error
  };
};
